/**
 * Attachment Service
 * 
 * Responsible for attachment upload and linking operations.
 * Follows Single Responsibility Principle and uses 3-step flow:
 * 1. Get pre-signed S3 URL
 * 2. Upload to S3
 * 3. Link to record
 */

import { FetchUtils } from '../utils/fetch-utils.js';
import { ExchangeUtils } from '../utils/exchange-utils.js';
import { AttachmentExtractor } from '../extractors/attachment-extractor.js';

/**
 * Handles S3 upload URL generation
 */
class S3UrlGenerator {
  static async getUploadUrl(attachmentName) {
    return FetchUtils.put('/api/attachment/s3', {
      attachmentName
    });
  }
}

/**
 * Handles file upload to S3
 */
class S3Uploader {
  /**
   * Upload file to S3 using pre-signed URL
   */
  static async upload(signedUploadUrl, fileContent, contentType) {
    // Convert base64 content to binary if needed
    const binaryContent = typeof fileContent === 'string' 
      ? Uint8Array.from(atob(fileContent), c => c.charCodeAt(0))
      : fileContent;
      
    const { getToken } = await import('../auth.js');
    const token = getToken();
    
    const response = await fetch(signedUploadUrl, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': contentType,
        'x-amz-date': new Date().toISOString()
      },
      body: binaryContent,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to upload file to S3: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
  }
}

/**
 * Handles linking attachments to records
 */
class AttachmentLinker {
  static async linkToRecord(entity, recordId, attachments) {
    const endpoint = `/api/${entity}/attachments`;
    const queryParam = `${entity}Id=${recordId}`;
    
    return FetchUtils.post(`${endpoint}?${queryParam}`, {
      attachments
    });
  }
}

/**
 * Processes individual attachments
 */
class AttachmentProcessor {
  /**
   * Process a single regular attachment
   */
  static async processRegularAttachment(item, attachment) {
    try {
      // Get attachment content from Outlook
      const attachmentContent = await AttachmentExtractor.getAttachmentContent(item, attachment.id);
      
      // Get pre-signed upload URL and upload to S3
      const s3Response = await S3UrlGenerator.getUploadUrl(attachment.name);
      await S3Uploader.upload(
        s3Response.signedUploadUrl, 
        attachmentContent.content, 
        attachment.contentType
      );
      
      return {
        _id: s3Response.attachment._id,
        value: attachment.name
      };
    } catch (error) {
      console.error(`Failed to process attachment ${attachment.name}:`, error);
      return null;
    }
  }

  /**
   * Process email as .eml file
   */
  static async processEmailMsgFile(item) {
    try {
      // Get email as .eml file (Office.js getAsFileAsync returns EML format)
      const emailMsgContent = await AttachmentExtractor.getEmailAsMsgFile(item);
      const emailMsgName = `${item.subject || 'Email'}.eml`;
      
      // Get pre-signed upload URL and upload to S3
      const s3Response = await S3UrlGenerator.getUploadUrl(emailMsgName);
      await S3Uploader.upload(
        s3Response.signedUploadUrl, 
        emailMsgContent, 
        'message/rfc822'
      );
      
      return {
        _id: s3Response.attachment._id,
        value: emailMsgName
      };
    } catch (error) {
      console.error('Failed to process email .eml file:', error);
      return null;
    }
  }
}

/**
 * Validates Exchange capabilities
 */
class CapabilityValidator {
  static validate() {
    const capabilities = ExchangeUtils.detectCapabilities();
    
    if (!capabilities.supportsAttachmentContent) {
      return {
        supported: false,
        error: `Attachment upload not supported on this Exchange server version (${capabilities.outlookVersion}). Requires Mailbox API 1.8 or higher.`,
        exchangeVersion: capabilities.outlookVersion
      };
    }
    
    return { supported: true };
  }
}

/**
 * Filters and selects attachments to process
 */
class AttachmentSelector {
  static select(allAttachments, selectedAttachments) {
    // Check if email .msg file is selected
    const emailMsgSelected = selectedAttachments.includes('email-msg-file');
    
    // Filter to only selected regular attachments (non-inline)
    const regularAttachments = allAttachments.filter(att => 
      selectedAttachments.includes(att.id) && !att.isInline
    );
    
    return { emailMsgSelected, regularAttachments };
  }
}

/**
 * Main Attachment Service
 * Follows Single Responsibility Principle
 */
export class AttachmentService {
  /**
   * Step 1: Get pre-signed upload URL from API
   */
  static async getS3UploadUrl(attachmentName) {
    return S3UrlGenerator.getUploadUrl(attachmentName);
  }
  
  /**
   * Step 2: Upload file directly to S3 using pre-signed URL
   */
  static async uploadToS3(signedUploadUrl, fileContent, contentType) {
    return S3Uploader.upload(signedUploadUrl, fileContent, contentType);
  }
  
  /**
   * Step 3: Link attachments to a specific record/entity
   */
  static async linkAttachmentsToRecord(entity, recordId, attachments) {
    return AttachmentLinker.linkToRecord(entity, recordId, attachments);
  }
  
  /**
   * Process attachments using the clean 3-step flow
   * Orchestrates the entire attachment processing workflow
   */
  static async processAttachments(item, activityId, selectedAttachments = null) {
    try {
      // Validate Exchange capabilities
      const validation = CapabilityValidator.validate();
      if (!validation.supported) {
        return {
          success: false,
          error: validation.error,
          attachments: [],
          totalProcessed: 0,
          totalAttempted: 0,
          exchangeVersion: validation.exchangeVersion,
          unsupportedFeature: true
        };
      }
      
      // Early return if no attachments selected
      if (!selectedAttachments || selectedAttachments.length === 0) {
        return {
          success: true,
          attachments: [],
          totalProcessed: 0,
          totalAttempted: 0
        };
      }
      
      // Extract all attachments from item
      const allAttachments = await AttachmentExtractor.extractAttachments(item);
      
      // Select attachments to process
      const { emailMsgSelected, regularAttachments } = 
        AttachmentSelector.select(allAttachments, selectedAttachments);
      
      const processedAttachments = [];
      let totalAttempted = regularAttachments.length;
      
      // Process email .msg file if selected
      if (emailMsgSelected) {
        totalAttempted++;
        const msgResult = await AttachmentProcessor.processEmailMsgFile(item);
        if (msgResult) {
          processedAttachments.push(msgResult);
        }
      }
      
      // Process regular attachments
      for (const attachment of regularAttachments) {
        const result = await AttachmentProcessor.processRegularAttachment(item, attachment);
        if (result) {
          processedAttachments.push(result);
        }
      }
      
      // Link all successfully processed attachments to the activity
      if (processedAttachments.length > 0) {
        await AttachmentLinker.linkToRecord('activity', activityId, processedAttachments);
      }
      
      return {
        success: true,
        attachments: processedAttachments,
        totalProcessed: processedAttachments.length,
        totalAttempted: totalAttempted
      };
      
    } catch (error) {
      console.error('Attachment processing failed:', error);
      return {
        success: false,
        error: error.message,
        attachments: [],
        totalProcessed: 0,
        totalAttempted: 0
      };
    }
  }
}

